import React, {useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {
    useMediaQuery,
    useTheme,
    Drawer,
    List,
    ListItemIcon,
    ListItemText,
    Box,
    Typography,
    IconButton,
    AppBar,
    Toolbar,
    ListItemButton,
    Button
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import PetsIcon from '@mui/icons-material/Pets';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Authentication from "../Authentication";
import {useAuth0} from "@auth0/auth0-react";
import {GlobalStateContext} from "../state/GlobalStateProvider";
import {useTenantConfig} from "../security/tenant-config-context";
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import {useReservationAPI} from "../service/useReservationAPI";


const SideNav = () => {
    const navigate = useNavigate();
    const {t, i18n} = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const { getMyReservations } = useReservationAPI();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const {loginWithRedirect, getAccessTokenSilently, isAuthenticated, logout} = useAuth0();
    const [showNavigation, setShowNavigation] = useState(false);
    const {setPartOfGlobalState} = useContext(GlobalStateContext);
    const {cfg} = useTenantConfig();
    const website = cfg.ui?.website;
    const logoUrl = cfg.ui?.logoUrl;
    const name = cfg.name;
    const isDogBusiness = cfg?.animalTypes?.includes('DOG');
    const isAppointmentFlow = cfg?.businessTypes?.includes('APPOINTMENT_BASED');
    // Language toggle function
    const toggleLanguage = () => {
        const newLang = i18n.language === 'en' ? 'fr' : i18n.language === 'fr' ? 'nl' : 'en';
        i18n.changeLanguage(newLang).then();
    };

    useEffect(() => {
        if (!isMobile) {
            setDrawerOpen(true);
        }

        const checkTokenExpiry = async () => {
            try {
                await getAccessTokenSilently();
            } catch (error) {
                await loginWithRedirect();
            }
        };

        if (isAuthenticated) {
            checkTokenExpiry().then();

            const interval = setInterval(() => {
                checkTokenExpiry().then();
            }, 60 * 1000); // Check every minute

            return () => clearInterval(interval);

        }
    }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect, logout, isMobile]);

    useEffect(() => {
        if (isAuthenticated) {
            getMyReservations()
                .then(values => {
                    if (values && values.length > 0) {
                        setShowNavigation(true)
                    }
                })

        }
    }, [isAuthenticated])

    const handleDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    const handleNavigation = (to) => {
        if (isMobile) {
            setDrawerOpen(false)
        }
        navigate(to)
    }

    function clearReservationMetaAndNavigate() {
        setPartOfGlobalState(setPartOfGlobalState('reservationMeta', {
            fromDate: undefined,
            toDate: undefined,
            animalsInReservationIds: [],
            checkInHour: undefined,
            checkOutHour: undefined,
            selectedCombo: undefined
        }))
        navigate('/nieuwe-reservatie');
    }

    const drawerContent = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%', // Full height of the parent container
            }}
        >
            {/* Top Section */}
            <Box
                sx={{flexGrow: 1, padding: 2, display: 'flex', flexDirection: 'column', alignItems: 'center'}}
            >
                {(() => {
                    const isDefaultLogo = typeof logoUrl === 'string' && logoUrl.includes('https://tailvation.be/logo');
                    if (!isDefaultLogo && logoUrl) {
                        return (
                            <a href={website}>
                                <img src={logoUrl} alt="Company Logo" style={{maxWidth: '100%', maxHeight: '60px'}}/>
                            </a>
                        );
                    }
                    return (
                        <Box sx={{width: '100%', maxWidth: 220}}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: 60,
                                    borderRadius: 2,
                                    bgcolor: 'grey.100',
                                    border: '2px dashed',
                                    borderColor: 'grey.400',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    mb: 0.5
                                }}
                            >
                                <ImageOutlinedIcon sx={{color: 'grey.500'}}/>
                                <Typography variant="caption" sx={{display: 'block', textAlign: 'center', color: 'text.secondary'}}>
                                    {t('yourLogoHere')}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })()}
                <Typography color="secondary">
                    {name}
                </Typography>
                {showNavigation && (<List>
                    {!isAppointmentFlow && (<ListItemButton onClick={() => handleNavigation("/mijn-reservaties")} data-testid="nav-reservations">
                        <ListItemIcon><HomeIcon color="primary"/></ListItemIcon>
                        <ListItemText primary={t('myReservations')}/>
                    </ListItemButton>)}
                    {isAppointmentFlow && (<ListItemButton onClick={() => handleNavigation("/mijn-afspraken")} data-testid="nav-appointments">
                        <ListItemIcon><HomeIcon color="primary"/></ListItemIcon>
                        <ListItemText primary={t('myAppointments')}/>
                    </ListItemButton>)}
                    <ListItemButton onClick={() => handleNavigation("/mijn-katten")} data-testid="nav-animals">
                        <ListItemIcon><PetsIcon color="primary"/></ListItemIcon>
                        <ListItemText primary={t(isDogBusiness ? 'myDogs' : 'myCats')}/>
                    </ListItemButton>
                    {!isAppointmentFlow && (<ListItemButton onClick={() => handleNavigation("/mijn-dierenarts")} data-testid="nav-veterinary">
                        <ListItemIcon><HealthAndSafetyIcon color="primary"/></ListItemIcon>
                        <ListItemText primary={t('myVet')}/>
                    </ListItemButton>)}
                    <ListItemButton onClick={() => handleNavigation("/mijn-profiel")} data-testid="nav-profile">
                        <ListItemIcon><AccountCircleIcon color="primary"/></ListItemIcon>
                        <ListItemText primary={t('myProfile')}/>
                    </ListItemButton>
                    {!isAppointmentFlow && (<ListItemButton onClick={() => handleNavigation("/noodcontact")} data-testid="nav-emergency-contact">
                        <ListItemIcon><ReportProblemIcon color="primary"/></ListItemIcon>
                        <ListItemText primary={t('emergencyContact')}/>
                    </ListItemButton>)}
                </List>)}

                {showNavigation && (<Button variant="contained" onClick={() => clearReservationMetaAndNavigate()} data-testid="nav-make-reservation">
                    {t(isAppointmentFlow ? 'makeAppointment' : 'makeReservation')}
                </Button>)}
            </Box>

            {/* Bottom Section */}
            <Box
                sx={{
                    padding: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderTop: '1px solid', // Optional divider style
                    borderColor: 'rgba(0, 0, 0, 0.12)' // Optional divider color
                }}
            >
                {/* Authentication Section */}
                <Button variant="outlined" onClick={toggleLanguage}>
                    {t("language")} ({(i18n.language || "en").toUpperCase()})
                </Button>
                <Authentication/>
            </Box>
        </Box>
    );

    return (
        <div>
            <AppBar position="fixed" sx={{background: 'transparent', boxShadow: 'none'}}>
                <Toolbar>
                    <IconButton
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{mr: 2}}
                    >
                        <MenuIcon/>
                    </IconButton>
                </Toolbar>
            </AppBar>
            <Drawer
                variant={isMobile ? "temporary" : "persistent"}
                open={drawerOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true,
                }}
            >
                <Toolbar>
                    <IconButton
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{mr: 2}}
                    >
                        <MenuIcon/>
                    </IconButton>
                </Toolbar>
                {drawerContent}
            </Drawer>
            {isMobile && <Toolbar/>} {/* Additional toolbar to prevent content overlap under the AppBar */}
        </div>
    );
}

export default SideNav;
